import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useScrollPosition } from 'react-use-scroll-position'


import { throttle, sessionstorage, $cookie } from '$utils'
import $dom from './util'

import { UseIntentDetailGift } from './modules/intent-modal/detail-gift'
import { UseIntentOrder } from './modules/intent-modal/order'
import { UseIntentDiscountNotifyGuide } from './modules/intent-modal/discount-notify-guide'
import { UseModalSuccess } from './modules/intent-modal/success'

import {
  getOtherScreenData, favoriteConfirm, favoriteAdd, favoriteRemove, serviceFee
} from './service'

const isProd = process.env.NODE_ENV === 'production'

// 详情页初始化、存储session共享数据（命令式调用弹窗通过render向页面渲染节点，无法共享store）
const initDefaultData = ({ carId }) => {
  const dispatch = useDispatch()
  const SHOW_DETAIL_LAYER_MASK = 'SHOW_DETAIL_LAYER_MASK' // 在详情页种植会话SESSION 保证弹窗提交后，其他逻辑的弹窗不出现
  const city = useSelector(store => store.city)
  const setDetailData = () => {
    const detailData = {
      phoneSourcePrefix: `${city?.fr}${city?.ino ? '_异地' : ''}`,
      cityAbbr: city?.city,
      cityName: city?.city_name,
      fr: city?.fr,
      ino: city?.ino
    }
    sessionstorage.set('detailData', detailData)
    sessionstorage.set('car_id', carId)
    sessionstorage.set('pageLoadTime', (new Date()).getTime()) // 记录一个session内线索首次提交
    sessionstorage.set('ino', city?.ino) // 是否是未开站城市
    sessionstorage.set(SHOW_DETAIL_LAYER_MASK, true)
  }
  useEffect(() => {
    dispatch({ type: 'SET_BASE_INFO', data: { header_fixed: false } })
    setDetailData()
  }, [])

  useEffect(() => { setDetailData() }, [city.city])

  useEffect(() => {
    const successParam = sessionstorage.get('loginBackModalInfo')
    if (successParam && $cookie.get('rrc_login_phone') && $cookie.get('rrc_login_token')) {
      new UseModalSuccess(successParam).open()
    }
    sessionstorage.del('loginBackModalInfo')
  }, [])
}

// title、keywords、description
const usePageMeta = ({ basicInfo, tags }) => {
  const title0 = `二手${basicInfo.car_model}_${basicInfo.licensed_city}二手${basicInfo.car_series}_${basicInfo.price}万-人人车`
  let title1 = title0
  if (tags && tags.length > 0) {
    title1 = `${tags[0] ? `【${tags[0].txt}】` : ''}${basicInfo.licensed_city}二手${basicInfo.car_model}_${basicInfo.price}万_${basicInfo?.uses_time}年_二手${basicInfo.car_series}_-人人车`
  }
  const pageMeta = {
    title: /[13579]$/g.test(basicInfo.rrc_id) ? title0 : title1,
    keywords: `${basicInfo.licensed_city}${basicInfo.car_series}二手车，${basicInfo.licensed_city}二手${basicInfo.car_series}，${basicInfo.price}万，${basicInfo.mileage}万公里，${basicInfo.car_color}，${basicInfo.car_model}，${basicInfo.car_series}二手车`,
    description: `${basicInfo.licensed_city}二手${basicInfo.car_series}-${basicInfo.car_model},购买时间:${basicInfo.licensed_date},表显里程:${basicInfo.mileage}公里,售价:${basicInfo.price}万,颜色：${basicInfo.car_color}。详询4000350113。已通过质量检测,保证车况真实透明,绝无事故车,有质保。`
  }
  return [pageMeta]
}

// 滚动>40时显示anchor
const useFixedAnchor = () => {
  const [fixedDisplay, setFixedDisplay] = useState(false)
  const { y } = useScrollPosition()
  useEffect(() => { throttle(setFixedDisplay(y > 40), 500) }, [y])
  return [fixedDisplay]
}

// 15s限时礼包弹窗
const usePopGift = ({ carId, content }) => {
  const guideInstance = useRef()
  const { phone } = useSelector(store => store.user)
  const { city } = useSelector(store => store)
  const { fr, ino } = city
  const basicInfo = content.basic_info
  const { sold } = basicInfo
  // 是否展示弹层
  const canShowPopWin = () => {
    // 下架车不显示, 特殊 fr(2309) 不展示,
    if (sold) { return false }
    if (['2309'].indexOf(fr) !== -1) { return false }
    return true
  }
  useEffect(() => {
    guideInstance.current = setTimeout(() => {
      if (!$dom.hasPopLayer() && canShowPopWin()) {
        const submitParams = {
          mobile_number: phone || '',
          phone_source: $dom.getPhoneSource(),
          submit_sources: '咨询-PC-详情页-15'
        }
        const otherParams = { ino, basicInfo }
        new UseIntentDetailGift({ submitParams, otherParams }).open()
        // 更新弹层次数、更新用户浏览次数
        $dom.intentModalShow()
        $dom.updateUserViewCarCount(carId)
      }
    }, isProd ? 15000 : 5000)
    return () => { clearTimeout(guideInstance.current) }
  }, [])
}

// 60s详情页线索引导弹层
// 单个车源详情页停留时长60s，弹出预约看车、咨询、降价、砍价弹层
const usePopWin = ({ carId, content, otherData }) => {
  const guideInstance = useRef()
  const user = useSelector(store => store.user)
  const { city } = useSelector(store => store)
  const { fr, ino } = city
  const basicInfo = content.basic_info
  const { sold, price, adjust_data } = basicInfo
  // 是否展示弹层
  const canShowPopWin = () => {
    // 下架车不显示, 特殊 fr(2309) 不展示,
    if (sold) { return false }
    if (['2309'].indexOf(fr) !== -1) { return false }
    // pc端总共最多弹7次, 同一辆车详情页只允许打开1次60s弹层
    if ($dom.intentModalShowTimes() > 7 || $dom.getUserViewCarCount(carId) > 0) {
      return false
    }
    return true
  }
  // 价格是否合理
  // const isReasonablePrice = true
  // console.log(
  //   adjust_data
  // )
  const isReasonablePrice = adjust_data?.has_adjust && price <= adjust_data?.individual_high_price
  useEffect(() => {
    const otherParams = { ino, basicInfo, owner: otherData?.owner }
    guideInstance.current = setTimeout(() => {
      if (!$dom.hasPopLayer() && canShowPopWin()) {
        // 价格合理--预约看车;价格不合理--降价提醒引导弹窗
        if (isReasonablePrice) {
          const submitParams = {
            mobile_number: user.phone || '',
            phone_source: $dom.getPhoneSource(),
            owner_price: `${price}`
          }
          new UseIntentOrder({ submitParams: { ...submitParams, submit_sources: '预约-PC-详情页-1' }, otherParams, isPopWin: true }).open()
          global.$rbl.push({
            event: 'pc-car-detail-详情页线索引导弹层-预约看车-弹出',
            content: {
              param: {
                car_id: carId
              }
            }
          })
        } else {
          const submitParams = {
            mobile_number: user.phone || '',
            phone_source: $dom.getPhoneSource('降价通知'),
            owner_price: `${price}`,
            submit_sources: Number(basicInfo.sell_on_consignment) === 0 ? '降价-PC-详情页-1' : '申领补贴-PC-详情页-5',
            model_name: 'discount_notify'
          }
          new UseIntentDiscountNotifyGuide({ submitParams: { ...submitParams }, otherParams }).open()
          global.$rbl.push({
            event: 'pc-car-detail-详情页线索引导弹层-降价通知-弹出',
            content: {
              param: {
                car_id: carId
              }
            }
          })
        }
        // 更新弹层次数、更新用户浏览次数
        $dom.intentModalShow()
        $dom.updateUserViewCarCount(carId)
      }
    }, isProd ? 60000 : 10000)

    return () => {
      clearTimeout(guideInstance.current)
    }
  }, [])
}
// 页面数据获取
const usePageData = ({ content, carId }) => {
  const { city } = useSelector(store => store)
  const basicInfo = content.basic_info

  const [otherData, setOtherData] = useState() // 非首屏数据 otherData
  const [servicePriceConfig, setServicePriceConfig] = useState({}) // 非首屏数据 otherData

  const breadOption = {
    bPinyin: basicInfo.b_pinyin,
    brand: basicInfo.brand,
    sPinyin: basicInfo.s_pinyin,
    series: basicInfo.car_series,
    cityName: city.city_name,
    cityAbbr: city.city,
    title: basicInfo.title
  }

  const getOtherData = async () => {
    try {
      const result = await getOtherScreenData({ type: 'detail', cid: carId, caller: 'pc' })
      setOtherData(result.data)
      // 补充信息页需要车主的城市地址信息，目前只有这一个地方用到，避免大量传参，存储到sessionStorage中
      sessionstorage.set('ownercity', result?.data?.owner?.city)
      sessionstorage.set('owneraddress', result?.data?.owner?.address)
    } catch (error) {
      setOtherData(error?.data?.data)
    }
  }

  const getServiceFee = async () => {
    try {
      const fee = await serviceFee({ cid: carId })
      setServicePriceConfig(fee.data)
    } catch (error) {
      setServicePriceConfig({})
    }
  }

  const init = () => {
    setTimeout(() => {
      getOtherData()
      getServiceFee()
    }, 0)
  }
  useEffect(() => { init() }, [])

  return [otherData, breadOption, servicePriceConfig]
}
// 收藏
const useFavorite = ({ carId }) => {
  const dispatch = useDispatch()
  const { phone, token } = useSelector(store => store.user)
  const [isFavorite, setFavorite] = useState(false)
  const favoriteParam = {
    token,
    car_id: carId,
    os: 'pc',
    device_type: 'pc',
    record_data: carId,
    record_type: '1'
  }

  const confirmFavorite = async () => {
    if (!phone || !token) {
      setFavorite(false)
      return false
    }
    try {
      const result = await favoriteConfirm(favoriteParam)
      setFavorite(result.data.is_fav)
      return true
    } catch (error) {
      return false
    }
  }

  const changeFavorite = async () => {
    if (!phone || !token) {
      dispatch({ type: 'SET_BASE_INFO', data: { show_login: true } })
    } else {
      try {
        if (!isFavorite) {
          await favoriteAdd(favoriteParam)
          new UseModalSuccess({ msg: '收藏成功' }).open()
        } else {
          await favoriteRemove(favoriteParam)
        }
        setFavorite(!isFavorite)
      } catch (error) {
        setFavorite(false)
      }
    }
  }

  useEffect(() => { confirmFavorite() }, [carId])

  return [isFavorite, changeFavorite]
}
export {
  initDefaultData, usePopGift, usePopWin, useFixedAnchor, usePageData, useFavorite, usePageMeta
}
